/**
 * /logistica/entregas — Lista de OrdensEntrega.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiPlus, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  listarOes,
  SITUACAO_OE_LABELS,
  SITUACAO_OE_TONES,
} from "../../../services/logistica";
import { PageContainer } from "../../../styles/shared";

import NovaOeForm from "./NovaOeForm";

import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";


export default function EntregasLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const podePlanejar = isMaster || hasAllPermissions(["logistica:planejar"]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [criando, setCriando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await listarOes(request, { situacao: filtroSituacao || undefined });
      setItems(Array.isArray(p) ? p : p?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar entregas.");
      setItems([]);
    } finally { setLoading(false); }
  }, [request, filtroSituacao]);

  useEffect(() => { carregar(); }, [carregar]);

  const kpis = useMemo(() => {
    const pendentes = items.filter((o) => o.situacao === "PENDENTE").length;
    const carregadas = items.filter((o) => o.situacao === "CARREGADA").length;
    const entregues = items.filter((o) => o.situacao === "ENTREGUE").length;
    const naoEntregues = items.filter((o) => o.situacao === "NAO_ENTREGUE").length;
    return [
      { label: "Total", value: items.length, tone: "neutral" },
      { label: "Pendentes", value: pendentes, tone: pendentes > 0 ? "warning" : "neutral" },
      { label: "Carregadas", value: carregadas, tone: carregadas > 0 ? "info" : "neutral" },
      { label: "Entregues", value: entregues, tone: "success" },
      { label: "Não entregues", value: naoEntregues, tone: naoEntregues > 0 ? "danger" : "neutral" },
    ];
  }, [items]);

  return (
    <PageContainer>
      <EntityHeader
        title="Ordens de entrega"
        subtitle="OEs cadastradas (ADR-0026)"
        actions={[
          // Ação só para quem realmente pode planejar — o endpoint exige
          // `logistica:planejar` e mostrar o botão a quem só tem `view`
          // rende 403 na cara do usuário.
          ...(podePlanejar
            ? [{
                label: "Nova OE",
                icon: <FiPlus size={14} />,
                onClick: () => setCriando(true),
              }]
            : []),
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} />,
            onClick: carregar,
          },
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 240 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Situação</span>
          <Select
            value={filtroSituacao}
            onChange={(e) => setFiltroSituacao(e.target.value)}
          >
            <option value="">Todas</option>
            {Object.entries(SITUACAO_OE_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </Select>
        </label>
      </Card>

      {error && <ErrorMsg><FiAlertTriangle size={14} /> {error}</ErrorMsg>}

      <Card>
        <SectionTitle>{loading ? "Carregando..." : `${items.length} OE(s)`}</SectionTitle>
        {!loading && items.length === 0 && <EmptyMsg>Nenhuma OE.</EmptyMsg>}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Carga</th>
                  <th>Endereço</th>
                  <th>Situação</th>
                  <th>Entregue em</th>
                </tr>
              </thead>
              <tbody>
                {items.map((oe) => (
                  <tr
                    key={oe.id}
                    onClick={() => navigate(`/logistica/entregas/${oe.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><strong>{oe.numero}</strong></td>
                    <td>{oe.pedido_numero}</td>
                    <td>{oe.pedido_cliente_nome}</td>
                    <td>{oe.carga_numero || "—"}</td>
                    <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={oe.endereco_entrega}>
                      {oe.endereco_entrega}
                    </td>
                    <td>
                      <Pill $tone={SITUACAO_OE_TONES[oe.situacao] || "neutral"}>
                        {SITUACAO_OE_LABELS[oe.situacao] || oe.situacao}
                      </Pill>
                      {oe.assinada && <Pill $tone="success" style={{ marginLeft: 6 }}>Assinada</Pill>}
                      {/* CRM-38: assinou quem não estava entre os autorizados
                          do cliente e ninguém da logística conferiu ainda. */}
                      {oe.assinante_autorizado === false && !oe.assinatura_conferida_em && (
                        <Pill $tone="warning" style={{ marginLeft: 6 }}>Assinante a conferir</Pill>
                      )}
                    </td>
                    <td>{oe.entregue_em ? fmtData(oe.entregue_em) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
      <NovaOeForm
        aberto={criando}
        onFechar={() => setCriando(false)}
        onCriada={carregar}
      />
    </PageContainer>
  );
}
